import { AnimatedContentLoader, Button, SearchInput } from '@root/components'
import { collectFromPages, useInfiniteTags } from '@root/query'
import { ITag } from '@root/query/tags'
import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { FaPlus } from 'react-icons/fa'
import styled from 'styled-components'
import TagsList from './TagsList'
import { TagFormModal } from './TagFormModal'
import TagDeleteModal from './TagDeleteModal'

const TagsDetails: React.FC = () => {
  const [isTagFormModalOpen, setIsTagFormModalOpen] = useState(false)
  const [isTagDeleteModalOpen, setIsTagDeleteModalOpen] = useState(false)
  const [selectedTag, setSelectedTag] = useState<ITag | undefined>(undefined)
  const [search, setSearch] = useState<string>('')

  const [t] = useTranslation()

  const { data, isLoading, hasNextPage, fetchNextPage } = useInfiniteTags({ search })

  const onTagFormModalClose = () => {
    setIsTagFormModalOpen(false)
  }

  const onTagDeleteModalClose = () => {
    setIsTagDeleteModalOpen(false)
  }

  const tags = collectFromPages(data)

  const onCreateNewTag = () => {
    setSelectedTag(undefined)
    setIsTagFormModalOpen(true)
  }

  const onEditTag = (tag: ITag) => {
    setSelectedTag(tag)
    setIsTagFormModalOpen(true)
  }

  const onDeleteTag = (tag: ITag) => {
    setSelectedTag(tag)
    setIsTagDeleteModalOpen(true)
  }

  return (
    <>
      <StyledPageContent>
        <StyledActionContainer>
          <SearchInput
            onSearch={(value: string) => {
              setSearch(value)
            }}
            placeholder={t('settings.tags.placeholder')}
          ></SearchInput>
          <Button icon={<FaPlus />} intent="success" type="button" onClick={() => onCreateNewTag()}>
            {t('settings.tags.new')}
          </Button>
        </StyledActionContainer>

        <StyledTagsWrapper>
          <AnimatedContentLoader
            isLoading={isLoading}
            isEmpty={tags.length === 0}
            isEmptyDescription={`${t('settings.tags.info')} ${t('settings.tags.empty')}`}
          >
            <TagsList
              tags={tags}
              onScrollBottom={() => {
                if (hasNextPage) {
                  fetchNextPage()
                }
              }}
              onEditTag={(tag: ITag) => {
                onEditTag(tag)
              }}
              onDeleteTag={(tag: ITag) => {
                onDeleteTag(tag)
              }}
            ></TagsList>
          </AnimatedContentLoader>
        </StyledTagsWrapper>
      </StyledPageContent>

      <TagFormModal
        isVisible={isTagFormModalOpen}
        handleOnClose={onTagFormModalClose}
        tag={selectedTag}
      />

      <TagDeleteModal
        isVisible={isTagDeleteModalOpen}
        tag={selectedTag}
        handleOnClose={onTagDeleteModalClose}
      ></TagDeleteModal>
    </>
  )
}

export default TagsDetails

const StyledPageContent = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${({ theme }) => theme.spacing.md}rem;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`

const StyledActionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md}rem;
`
const StyledTagsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${({ theme }) => theme.spacing.xl}rem;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`
